import { Drawer, Flex, FormStatus, InfoCard, Input, P, Tag } from '@dnb/eufemia';
import { Button } from '@dnb/eufemia';
import { filter } from '@dnb/eufemia/icons';
import { deepCopy } from '@portals/shared/common/utils';
import { TrackRoute } from '@portals/shared-frontend/components';
import { useCallback, useMemo } from 'react';

import HeroPage from '@/components/HeroPage';
import { HeroContent } from '@/components/HeroPage/HeroContent';
import { HeroContentMenu } from '@/components/HeroPage/HeroContentMenu';
import HeroMenu from '@/components/HeroPage/HeroMenu';
import { createDummyEvent } from '@/dummyData';
import { useBackend } from '@/hooks/useBackend';
import useSWRExpect404 from '@/hooks/useSWRExpect404';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { EventWithUserDataResponse } from '@/openapi/schema';
import EventList from '@/pages/explorer/events/EventList';

import ServiceDomainFilters, { SERVICE_DOMAIN_KEY } from '../filters/ServiceDomainFilter';
import TypeFilter, { CLASSIFICATION_FILTER_KEY } from '../filters/TypeFilter';
import { useCrossFilterCounts } from '../filters/useCrossFilterCounts';
import { useEventFilters } from './useEventFilters';

const DUMMY_EVENTS = new Array(5).fill(null).map((_, id) => createDummyEvent(id));

const FILTER_CONFIGS = [
  {
    key: 'typeFilter',
    getValue: (event: EventWithUserDataResponse) => event.classification,
  },
  {
    key: 'serviceDomainFilter',
    getValue: (event: EventWithUserDataResponse) => event.serviceDomain?.id,
  },
];

export default function EventExplorerPage() {
  const swrExpect404 = useSWRExpect404();
  const { data: events, isLoading, error, mutate } = useBackend('/events', {}, swrExpect404);
  const { data: serviceDomains } = useBackend('/service-now/service-domains');

  const { updateFilterInURL, clearAllFilters } = useUrlFilters();
  const { query, setQuery, filteredEvents, serviceDomainOptions, serviceDomainFilter, eventOptions, eventFilters } =
    useEventFilters(events, serviceDomains);

  const onFavoriteChange = useCallback(
    async (eventId: string, isFavorite: boolean) => {
      await mutate((events) => {
        if (!events) return;
        const updated = deepCopy(events);
        const event = updated.find((e) => e.id === eventId);
        if (event) event.isFavorite = isFavorite;
        return updated;
      }, false);
    },
    [mutate],
  );

  const eventClassificationTypes = useMemo(() => {
    const set = new Set<string>();
    if (!events) return [];
    set.add('all');
    events.forEach(({ classification }) => set.add(classification));
    return Array.from(set);
  }, [events]);

  const searchField = (
    <Input clear icon="loupe" on_change={({ value }) => setQuery(value)} placeholder="Search" stretch value={query} />
  );

  const filters = useMemo(
    () => ({
      typeFilter: eventFilters,
      serviceDomainFilter,
    }),
    [eventFilters, serviceDomainFilter],
  );

  const counts = useCrossFilterCounts(events ?? [], filters, FILTER_CONFIGS);

  const filterComponents = (
    <>
      <TypeFilter
        allApiCount={events?.length}
        classificationCount={counts['typeFilter']!}
        classifications={eventClassificationTypes}
      />
      <ServiceDomainFilters
        serviceDomainCount={counts['serviceDomainFilter']!}
        serviceDomainOptions={serviceDomainOptions}
      />
    </>
  );

  return (
    <div className="dark-mode">
      <HeroPage banner="explorer" skeleton={isLoading} title="Events Explorer">
        <TrackRoute name="Events Explorer" path="/explorer/events" type="events" />
        <HeroMenu>
          {searchField}
          <Button
            onClick={() => {
              clearAllFilters();
              setQuery('');
            }}
            space={{ top: 'small' }}
            variant="tertiary"
          >
            Clear Filters
          </Button>
          {filterComponents}
        </HeroMenu>
        <HeroContent>
          <HeroContentMenu>
            {searchField}
            <Drawer
              id="tagFilterDrawer"
              title="Filters"
              top="small"
              triggerAttributes={{ icon: filter, text: 'Filters', variant: 'tertiary' }}
            >
              {filterComponents}
            </Drawer>
            {(eventFilters.length > 0 || serviceDomains) && (
              <div>
                <Tag.Group label="Filters" top="x-small">
                  {eventFilters.map((tag) => (
                    <Tag
                      key={tag}
                      onClick={() =>
                        updateFilterInURL(
                          CLASSIFICATION_FILTER_KEY,
                          eventFilters.filter((t) => t !== tag),
                          eventOptions,
                        )
                      }
                      variant="removable"
                    >
                      {tag}
                    </Tag>
                  ))}
                  {serviceDomainFilter.map((serviceDomain) => (
                    <Tag
                      key={serviceDomain}
                      onClick={() =>
                        updateFilterInURL(
                          SERVICE_DOMAIN_KEY,
                          serviceDomainFilter.filter((sdf) => sdf !== serviceDomain),
                          serviceDomainOptions,
                        )
                      }
                      variant="removable"
                    >
                      {serviceDomainOptions.find((sdo) => sdo.selectedKey === serviceDomain)?.content}
                    </Tag>
                  ))}
                </Tag.Group>
              </div>
            )}
          </HeroContentMenu>
          <div>
            <P modifier="medium" space={{ bottom: 'medium' }}>
              Showing {filteredEvents.length ?? 0} of {events?.length ?? 0} events
            </P>
          </div>
          <Flex.Vertical align="stretch" gap="xx-small">
            {error ? (
              <FormStatus stretch text="Failed to fetch events." />
            ) : isLoading ? (
              <EventList events={DUMMY_EVENTS} onFavoriteChange={() => {}} />
            ) : filteredEvents.length === 0 ? (
              <InfoCard text="No events match the selected filters." />
            ) : (
              <EventList events={filteredEvents} onFavoriteChange={onFavoriteChange} />
            )}
          </Flex.Vertical>
        </HeroContent>
      </HeroPage>
    </div>
  );
}
